import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { deviceSettingsQuery, devicesTableQuery, eventsTableQuery, overviewQuery, post, printersTableQuery, type Row } from '../api/printeeng'
import { today, type Period } from '../api/securitee'
import { ApiError } from '../api/client'
import { Content } from '../ui/Shell'
import { DataTable, useTableState, type TableColumn } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { PeriodFilter } from '../securitee/DevicesPage'
import { deviceColumns, eventColumns, printerColumns, type Column } from './columns'
import { DeviceSettingsForm, PrinteengTabs, PrinterSettingsForm } from './Tabs'

/**
 * Cele trei liste Printeeng - `all_devices` (#allDevices), `printers`
 * (#allPrinters), `events` (#allEvents) - cu perioada din antet
 * (`PrinteengAllDevices.dataXhr`: azi), randul desfasurat cu cele patru file
 * si widgetul statiilor bifate (`multipleSelectDevicesSettings` /
 * `multipleSelectPrintersSettings`, cu Remove pentru imprimante).
 * Deasupra listei de masini, cele patru contoare din `getOverviewDashboard`
 * (`printeeng/dashboard/dashboard.php`), cu moneda.
 */

function Overview() {
  const q = useQuery(overviewQuery())
  if (!q.data) return null
  const c = q.data.counters
  const tiles: [string, string, string | number, string][] = [
    ['blue-madison', 'fa fa-print', c.monitored_devices, 'Monitored devices'],
    ['red-intense', 'fa fa-exclamation-triangle', c.excedeed_quota, 'Exceeded quota (30 days)'],
    ['green-haze', 'fa fa-file', c.total_pages, 'Printed pages (30 days)'],
    ['blue-mauve', 'fa fa-money-bill', `${c.total_costs} ${q.data.currency}`, 'Printing costs (30 days)'],
  ]
  return (
    <div className="row printeeng-overview" style={{ marginBottom: 10 }}>
      {tiles.map(([color, icon, number, label]) => (
        <div className="col-md-3" key={label}>
          <div className={`dashboard-stat ${color}`}>
            <div className="visual"><i className={icon}></i></div>
            <div className="details"><div className="number">{number}</div><div className="desc">{label}</div></div>
          </div>
        </div>
      ))}
    </div>
  )
}

function ListPage({ id, columns, query, defaultOrder, details, widget, top }: {
  id: string
  columns: Column[]
  query(r: { draw: number; start: number; length: number; orderColumn: number; orderName: string; orderDir: 'asc' | 'desc'; search: string } & Period): ReturnType<typeof devicesTableQuery>
  defaultOrder: number
  details?(row: Row, period: Period): ReactNode
  widget?(chosen: number[], shown: boolean, onHide: () => void, onSaved: () => void): ReactNode
  top?: ReactNode
}) {
  const client = useQueryClient()
  const [period, setPeriod] = useState<Period>(() => ({ from: today(), to: today() }))
  const [state, setState] = useTableState(id, { start: 0, length: 25, order: { column: defaultOrder, dir: 'asc' }, search: '' })
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [widgetHidden, setWidgetHidden] = useState(false)
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[defaultOrder]!
  const q = useQuery({
    ...query({ draw: 1, start: state.start, length: state.length, orderColumn: orderColumn.index, orderName: orderColumn.name ?? '', orderDir: state.order.dir, search: state.search, ...period }),
    placeholderData: keepPreviousData,
  })
  const rows = q.data?.data ?? []
  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target instanceof HTMLInputElement && target.classList.contains('oneDevice')) {
      const n = Number(target.value)
      setSelected((s) => { const next = new Set(s); if (target.checked) next.add(n); else next.delete(n); return next })
      setWidgetHidden(false)
    } else if (target instanceof HTMLInputElement && target.classList.contains('allDevices')) {
      setSelected(target.checked ? new Set(rows.map((r) => Number(r.ID))) : new Set())
      setWidgetHidden(false)
    }
  }
  return (
    <Content>
      <div id="content" onClick={onTableClick}>
        {top}
        <DataTable
          id={id}
          columns={columns as TableColumn[]}
          rows={rows.map((r) => ({ ...r, __checked: selected.has(Number(r.ID)) }))}
          total={q.data?.recordsTotal ?? 0}
          fetching={q.isFetching}
          error={q.isError ? (q.error as Error).message : null}
          onRetry={() => void q.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId)}
          headerButtons={<PeriodFilter period={period} onChange={setPeriod} />}
          details={details ? (row) => details(row, period) : undefined}
        >
          {widget ? widget([...selected], selected.size > 0 && !widgetHidden, () => setWidgetHidden(true), () => { setSelected(new Set()); void client.invalidateQueries({ queryKey: ['printeeng'] }) }) : null}
        </DataTable>
      </div>
    </Content>
  )
}

function Widget({ shown, onHide, title, children }: { shown: boolean; onHide(): void; title: string; children: ReactNode }) {
  return (
    <div className="multiple_check_widget widget" style={{ display: shown ? undefined : 'none' }}>
      <div className="widget-header bg-module">
        <div className="widget-icon"><i className="fas fa-print"></i></div>
        <h2 className="widget-title">Selected Devices Options</h2>
        <div className="widget-action"><button className="btn hide-widget" type="button" onClick={onHide}><i className="fas fa-times"></i></button></div>
      </div>
      <div className="widget-content">
        <div style={{ width: '20%', display: 'inline-block', boxSizing: 'border-box' }}>
          <ul className="nav nav-metro-tabs tabs-left"><li data-class-nav="nav-content" className={shown ? 'active' : undefined}><a>{title}</a></li></ul>
        </div>
        <div className="nav-content" style={{ display: 'inline-block', width: '79%', boxSizing: 'border-box', verticalAlign: 'top', paddingLeft: 15 }}>
          {shown ? children : null}
        </div>
      </div>
    </div>
  )
}

async function send(path: string, fields: Record<string, string | number[]>, onDone: () => void): Promise<void> {
  try {
    const body = await post(path, fields)
    toast(body.message, body.status === 'OK' ? 'Success' : 'Error', body.status === 'OK' ? 'success' : 'error')
    if (body.status === 'OK') onDone()
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
  }
}

/** devices/multiple_select/devicesSettings.php - profilele se citesc prin prima masina bifata. */
function DevicesWidgetBody({ chosen, onSaved }: { chosen: number[]; onSaved(): void }) {
  const q = useQuery(deviceSettingsQuery(chosen[0]!))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Device</strong> settings</h4></header>
      <div className="panel-body">
        <DeviceSettingsForm profiles={q.data.profiles} initial={null} multi action="/printeeng/saveMultipleDevicesSettings" submit={(f) => void send('/printeeng/devices/settings', { ...f, devices: chosen }, onSaved)} />
      </div>
    </>
  )
}

export function PrinteengDevicesPage() {
  const columns = useMemo(() => deviceColumns(), [])
  return (
    <ListPage
      id="allDevices"
      columns={columns}
      query={devicesTableQuery}
      defaultOrder={2}
      top={<Overview />}
      details={(row, period) => <PrinteengTabs kind="devices" id={Number(row.ID)} period={period} />}
      widget={(chosen, shown, onHide, onSaved) => (
        <Widget shown={shown} onHide={onHide} title="Device settings">{chosen.length > 0 ? <DevicesWidgetBody chosen={chosen} onSaved={onSaved} /> : null}</Widget>
      )}
    />
  )
}

/** printers/multiple_select/printersSettings.php + removeMultiplePrinters */
function PrintersWidgetBody({ chosen, onSaved }: { chosen: number[]; onSaved(): void }) {
  return (
    <>
      <header className="panel-heading sm" data-color="theme-inverse"><h4><strong>Printers</strong> settings</h4></header>
      <div className="panel-body">
        <PrinterSettingsForm initial={null} currency="" action="/printeeng/saveMultiplePrintersSettings" submit={(f) => void send('/printeeng/printers/settings', { ...f, printers: chosen }, onSaved)} />
        <button type="button" className="btn btn-danger btn-sm remove_printers" onClick={() => { if (confirm('Are you sure?')) void send('/printeeng/printers/remove', { printers: chosen }, onSaved) }}>
          <i className="fas fa-trash"></i> Remove printers
        </button>
      </div>
    </>
  )
}

export function PrinteengPrintersPage() {
  const columns = useMemo(() => printerColumns(), [])
  return (
    <ListPage
      id="allPrinters"
      columns={columns}
      query={printersTableQuery}
      defaultOrder={2}
      details={(row, period) => <PrinteengTabs kind="printers" id={Number(row.ID)} period={period} />}
      widget={(chosen, shown, onHide, onSaved) => (
        <Widget shown={shown} onHide={onHide} title="Printers settings">{chosen.length > 0 ? <PrintersWidgetBody chosen={chosen} onSaved={onSaved} /> : null}</Widget>
      )}
    />
  )
}

export function PrinteengEventsPage() {
  const columns = useMemo(() => eventColumns(), [])
  return <ListPage id="allEvents" columns={columns} query={eventsTableQuery} defaultOrder={9} />
}
